import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { mapStudentsToRows } from '../utils/utils';
import { StudentRow } from '../types/ProvisionalPromotionTypes';
import { SnackbarSeverityType } from '../../../enums';
import userApi from '../../../apis/users';

const useProvisionalPromotion = (defaultAcademicYear: string) => {
  const { user: loggedInUser } = useSelector((state: any) => state.profile);
  const initialToastState = {
    open: false,
    message: '',
    severity: SnackbarSeverityType.success
  };
  const [grade, setGrade] = useState('');
  const [division, setDivision] = useState('');
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: SnackbarSeverityType;
  }>(initialToastState);
  const allRowsSelected =
    students.length > 0 && selectedIds.length === students.length;
  const isSomeRowsSelected =
    selectedIds.length > 0 && selectedIds.length < students.length;

  const updateToast = (newToast: typeof initialToastState) =>
    setToast(newToast);

  const handleSearch = useCallback(async () => {
    if (!loggedInUser?.school || !defaultAcademicYear) return;

    setLoading(true);
    setHasSearched(false);
    setSelectedIds([]);
    setStudents([]);
    try {
      const studentsData = await userApi.getStudentsByVisibilityGroup(
        loggedInUser.school,
        grade,
        division
      );
      const mapped = mapStudentsToRows(studentsData, defaultAcademicYear);
      setStudents(mapped);
      setHasSearched(true);
    } catch (error) {
      setToast({
        open: true,
        message: 'Failed to fetch students',
        severity: SnackbarSeverityType.error
      });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line
  }, [grade, division]);

  const onCellCheckboxClick = (id: string) => {
    setSelectedIds((prevSelectedRows) =>
      prevSelectedRows.includes(id)
        ? prevSelectedRows.filter((rowId) => rowId !== id)
        : [...prevSelectedRows, id]
    );
  };

  const onHeaderCheckboxClick = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      setSelectedIds(students.map((row) => row.id));
    } else {
      setSelectedIds([]);
    }
  };

  const onGradeChange = (newGrade: string) => {
    setGrade(newGrade);
    setDivision('');
    setStudents([]);
    setSelectedIds([]);
    setHasSearched(false);
  };

  const onDivisionChange = (newDivision: string) => {
    setDivision(newDivision);
    setStudents([]);
    setSelectedIds([]);
    setHasSearched(false);
  };

  return {
    grade,
    division,
    students,
    selectedIds,
    hasSearched,
    loading,
    toast,
    allRowsSelected,
    isSomeRowsSelected,
    setSelectedIds,
    setToast: updateToast,
    handleSearch,
    onHeaderCheckboxClick,
    onCellCheckboxClick,
    onGradeChange,
    onDivisionChange
  };
};

export default useProvisionalPromotion;
